import { useState } from "react";
import { useTranslation } from "react-i18next";
import CookieConsent from "react-cookie-consent";
import styles from "./CookieBanner.module.scss";
import { useCookieContext } from "../../context/CookieContext";
import { updateGtagConsent } from "../../utils/updateGtagConsent";
import { initGTM } from "../../utils/initGTM";
import { CookieSettings, ConsentStatus } from "../../types/cookie";

const CookieBanner = () => {
  const { t } = useTranslation("common");
  const { isOpen, closeBanner, cookieSettings, updateCookieSettings } =
    useCookieContext();
  const [showSettings, setShowSettings] = useState(false);
  const [tempSettings, setTempSettings] =
    useState<CookieSettings>(cookieSettings);

  const handleConsent = (status: ConsentStatus, settings?: CookieSettings) => {
    let finalSettings: CookieSettings;

    if (status === "accepted") {
      finalSettings = {
        necessary: true,
        analytics: true,
        marketing: true,
        preferences: true,
      };
    } else if (status === "declined") {
      finalSettings = {
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false,
      };
    } else {
      finalSettings = settings || cookieSettings;
    }

    updateCookieSettings(finalSettings);
    localStorage.setItem("cookieConsent", status);
    updateGtagConsent(finalSettings);

    if (finalSettings.analytics) {
      initGTM();
    }

    closeBanner();
    setShowSettings(false);
  };

  const toggleSetting = (key: keyof CookieSettings) => {
    if (key === "necessary") return;
    setTempSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      {isOpen && !showSettings && (
        <CookieConsent
          disableStyles={true}
          location="bottom"
          cookieName="cookieConsent"
          containerClasses={styles.cookieConsent}
          contentClasses={styles.content}
          buttonText={null}
          declineButtonText={null}
          enableDeclineButton={false}
          expires={365}
          visible={isOpen ? "show" : "hidden"}
        >
          <div className={styles.bannerContent}>
            <strong>{t("cookies.title")}</strong>
            <p className={styles.text}>
              {t("cookies.message")}{" "}
              <a href="/privacy" className={styles.link}>
                {t("cookies.privacyLink")}
              </a>
            </p>
            <div className={styles.customButtons}>
              <button
                className={styles.acceptButton}
                onClick={() => handleConsent("accepted")}
              >
                {t("cookies.buttons.acceptAll")}
              </button>
              <button
                className={styles.declineButton}
                onClick={() => handleConsent("declined")}
              >
                {t("cookies.buttons.decline")}
              </button>
              <button
                className={styles.customizeButton}
                onClick={() => {
                  setTempSettings(cookieSettings);
                  setShowSettings(true);
                }}
              >
                {t("cookies.buttons.customize")}
              </button>
            </div>
          </div>
        </CookieConsent>
      )}

      {showSettings && (
        <div className={styles.settingsModal}>
          <div className={styles.settingsContent}>
            <h3>{t("cookies.title")}</h3>

            <div className={styles.settingItem}>
              <div className={styles.settingHeader}>
                <span className={styles.settingTitle}>
                  {t("cookies.types.necessary.title")}
                </span>
                <label className={styles.toggleSwitch}>
                  <input type="checkbox" checked={true} disabled />
                  <span className={styles.slider}></span>
                </label>
              </div>
              <p className={styles.settingDescription}>
                {t("cookies.types.necessary.description")}
              </p>
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingHeader}>
                <span className={styles.settingTitle}>
                  {t("cookies.types.analytics.title")}
                </span>
                <label className={styles.toggleSwitch}>
                  <input
                    type="checkbox"
                    checked={tempSettings.analytics}
                    onChange={() => toggleSetting("analytics")}
                  />
                  <span className={styles.slider}></span>
                </label>
              </div>
              <p className={styles.settingDescription}>
                {t("cookies.types.analytics.description")}
              </p>
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingHeader}>
                <span className={styles.settingTitle}>
                  {t("cookies.types.marketing.title")}
                </span>
                <label className={styles.toggleSwitch}>
                  <input
                    type="checkbox"
                    checked={tempSettings.marketing}
                    onChange={() => toggleSetting("marketing")}
                  />
                  <span className={styles.slider}></span>
                </label>
              </div>
              <p className={styles.settingDescription}>
                {t("cookies.types.marketing.description")}
              </p>
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingHeader}>
                <span className={styles.settingTitle}>
                  {t("cookies.types.preferences.title")}
                </span>
                <label className={styles.toggleSwitch}>
                  <input
                    type="checkbox"
                    checked={tempSettings.preferences}
                    onChange={() => toggleSetting("preferences")}
                  />
                  <span className={styles.slider}></span>
                </label>
              </div>
              <p className={styles.settingDescription}>
                {t("cookies.types.preferences.description")}
              </p>
            </div>

            <div className={styles.settingsButtons}>
              <button
                className={styles.saveButton}
                onClick={() => handleConsent("custom", tempSettings)}
              >
                {t("cookies.buttons.save")}
              </button>
              <button
                className={styles.cancelButton}
                onClick={() => setShowSettings(false)}
              >
                {t("cookies.buttons.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
